'use client';

import { DateTime } from 'luxon';
import CustomDateRange from '@/components/filters/CustomDateRange';
import {
  arrowsOptionStyle,
  arrowsOptionsContainer,
  dropdownContainer,
  mobileRange,
  nextButton,
  prevButton,
  select,
} from './Select.css';
import {
  type SpecialRange,
  defaultDisplayedTimespans,
  findSpecialRange,
  nextWindow,
  prevWindow,
  timespans,
} from './timespans';
import { useLocale } from '@instamenu/context-i18n';
import { useMediaQuery } from 'react-responsive';
import { useState } from 'react';
import { DrawerDialog } from '../modals';
import { Button, Text } from '@instamenu/ui-system-next';
import { dateFormats, formatDate } from '@instamenu/formatters';
import { Check } from 'lucide-react';

type Timespan = {
  start: DateTime;
  end: DateTime;
};

type DateSelectProps = {
  onChange: (value: Timespan) => void;
  timespan: Timespan;
  displayedTimespans?: SpecialRange[];
};

export const DateSelect = function DateSelect(props: DateSelectProps) {
  const { onChange, timespan, displayedTimespans } = props;
  const { i18n } = useLocale();
  const isDesktop = useMediaQuery({ query: `(min-width: 768px)` });
  const [visible, setVisible] = useState(false);

  const allowedOptions = displayedTimespans ?? defaultDisplayedTimespans;
  const filteredTimespans = allowedOptions.length
    ? timespans.filter((t) => allowedOptions.includes(t.value))
    : timespans;

  const label = timespan.start.hasSame(timespan.end, 'day')
    ? formatDate(timespan.start, dateFormats.dateShorter)
    : formatDate(timespan.start, dateFormats.dateShorter) +
      ' - ' +
      formatDate(timespan.end, dateFormats.dateShorter);

  const specialRange = findSpecialRange({ start: timespan.start, end: timespan.end });

  const Options = (
    <>
      <div className={dropdownContainer}>
        <CustomDateRange timespan={timespan} onChange={onChange} />
        {!isDesktop && !specialRange && (
          <div className={mobileRange}>
            <>
              <Text css={{ marginLeft: 8 }} variant="body1">
                {label}
              </Text>
              <Button size="xs" onClick={() => setVisible(false)}>
                {i18n.t('ok')}
              </Button>
            </>
          </div>
        )}
        <div className={arrowsOptionsContainer}>
          {filteredTimespans.map((t, i) => (
            <div
              onClick={() => {
                onChange(t);
                setVisible(false);
              }}
              key={i}
              className={arrowsOptionStyle}
            >
              <div data-id={'date-select-option'}>{i18n.t(`timeSpan.${t.value}`)}</div>
              {specialRange === t && <Check size={24} color="#34ac68" />}
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const SelectContainer = (
    <div style={{ display: 'flex' }}>
      <div
        data-id="prev-button"
        className={prevButton}
        onClick={() => {
          onChange(prevWindow(timespan));
        }}
      ></div>
      <div
        data-id="date-select"
        onClick={() => setVisible(true)}
        style={{ flex: 1 }}
        className={select}
      >
        {label}
      </div>
      <div
        data-id="next-button"
        className={nextButton}
        onClick={() => onChange(nextWindow(timespan))}
      ></div>
    </div>
  );

  return (
    <DrawerDialog label={SelectContainer} arrows visible={visible} full setVisible={setVisible}>
      {Options}
    </DrawerDialog>
  );
};
